/** Listening-session tracker: shared types, timings and the stored-session helpers (#1650). Split from the tracker for the file-size gate. */

export type TrackEndClass = 'clean' | 'early_advance' | 'skip_failure' | 'user_skip' | 'repeat' | 'maestro_stop';
export type SessionOutcome = 'clean' | 'user_ended' | 'maestro_stop' | 'failed_start' | 'app_closed';
export type CloseReason = 'unload' | 'sleep' | 'next_launch';
/** Bumped when the repeat rule changes; sent with every end so the server can tell old rows from new. */
export const REPEAT_RULE = 2;

export interface PlaybackObservation {
  playbackState: string;
  trackId: string | null;
  position: number; // seconds
  duration: number; // seconds
  queueIndex: number;
  queueLength: number;
  repeatMode: string;
  /** Recent user transport command (play/pause/stop/seek/next/previous) within a few seconds. */
  userCommandRecent: boolean;
}

export interface SessionContext {
  context: 'album' | 'playlist' | 'tracks' | 'radio';
  queue_len: number;
  source: string | null;
  renderer_manufacturer: string | null;
  renderer_model: string | null;
  transport_type: string | null;
  renderer_kind: string | null;
}

export interface TrackerEmit {
  type: 'listening_session_start' | 'listening_session_end';
  at: number;
  metadata: Record<string, unknown>;
}

export const START_TIMEOUT_MS = 30_000;
export const STOP_CONFIRM_MS = 20_000;
export const PAUSE_END_MS = 15 * 60_000;
export const ADVANCE_REASON_WINDOW_MS = 4_000;
/** A restart-from-the-top must outlast this on the same track and queue slot to count as a repeat. */
export const REPEAT_CONFIRM_MS = 6_000;
/** No sample for this long (timers still tick every 5 s, 60 s when throttled) means the machine slept or froze. */
export const SLEEP_GAP_MS = 150_000;

/** The open session, small enough for localStorage. */
export interface TrackerSnapshot {
  v: 1;
  key: string;
  openedAt: number;
  lastAt: number;
  startLatency: number | null;
  ctx: SessionContext | null;
  counts: Record<string, number>;
}


export type Counts = Record<'tracks_clean' | 'tracks_early_advance' | 'tracks_skip_failure' | 'tracks_user_skip' | 'tracks_repeat' | 'tracks_maestro_stop', number>;
export const zero = (): Counts => ({ tracks_clean: 0, tracks_early_advance: 0, tracks_skip_failure: 0, tracks_user_skip: 0, tracks_repeat: 0, tracks_maestro_stop: 0 });
export const COUNT_KEY: Record<TrackEndClass, keyof Counts> = {
  clean: 'tracks_clean', early_advance: 'tracks_early_advance', skip_failure: 'tracks_skip_failure',
  user_skip: 'tracks_user_skip', repeat: 'tracks_repeat', maestro_stop: 'tracks_maestro_stop',
};

let keySeq = 0;
export function newSessionKey(now: number): string {
  try {
    const c = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto;
    if (c?.randomUUID) return c.randomUUID();
  } catch { /* fall through */ }
  keySeq += 1;
  return `ls-${now.toString(36)}-${keySeq}-${Math.random().toString(36).slice(2, 8)}`;
}

/** The end event for a session whose window is already gone (read back from a stored snapshot on the next launch). */
export function endFromSnapshot(s: TrackerSnapshot, reason: CloseReason = 'next_launch'): TrackerEmit {
  return {
    type: 'listening_session_end',
    at: s.lastAt,
    metadata: {
      session_key: s.key, ...(s.ctx || {}), outcome: 'app_closed', close_reason: reason, ended_at: s.lastAt, reached_end: false,
      start_latency_ms: s.startLatency, duration_ms: Math.max(0, s.lastAt - s.openedAt), repeat_rule: REPEAT_RULE, ...s.counts,
    },
  };
}

/** Narrow check for a stored value before it is trusted. */
export function isSnapshot(x: unknown): x is TrackerSnapshot {
  const s = x as Partial<TrackerSnapshot> | null;
  return !!s && s.v === 1 && typeof s.key === 'string' && s.key.length > 0 && typeof s.openedAt === 'number' && typeof s.lastAt === 'number'
    && !!s.counts && typeof s.counts === 'object';
}
